import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Landing from "@/pages/Landing";
import AirdropPortal from "@/pages/AirdropPortal";
import PayPage from "@/pages/PayPage";
import Presale from "@/pages/Presale";
import Mining from "@/pages/Mining";
import AdminPanel from "@/pages/AdminPanel";
import { WalletProvider } from "@/lib/wallet";
import { initLenis, scrollStore } from "@/lib/scroll";
import "@/App.css";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink px-6 text-center">
          <p className="font-mono text-sm text-sgreen">Something broke</p>
          <p className="max-w-md text-sm text-mut">
            An unexpected error occurred. Refresh the page to try again.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      if (scrollStore.lenis) scrollStore.lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

const META = {
  "/": {
    title: "B&W Pay — Accept USDC Payments on Solana | 1% Flat Fee, Non-Custodial",
    desc: "B&W Pay is the non-custodial stablecoin payment gateway on Solana. Accept USDC with a 1% flat fee, instant settlement and no chargebacks.",
  },
  "/presale": {
    title: "$BNW Presale — Buy B&W with SOL or USDC | B&W Pay",
    desc: "The $BNW presale is live: three rounds from $0.20, 250,000 tokens, $70,000 target. Pay with SOL or USDC — tokens delivered automatically on-chain.",
  },
  "/airdrop": {
    title: "$BNW Airdrop — Earn BWXP, Convert 100:1 to $BNW | B&W Pay",
    desc: "Join the B&W Pay airdrop: complete quests and the daily $BNW trend mission to earn BWXP. 100 BWXP = 1 $BNW, converting on 18 November 2026.",
  },
  "/mining": {
    title: "USDC Mining — Mine Real USDC Every 24 Hours | B&W Pay",
    desc: "Start free and mine 0.1 USDC every 24 hours. Boost to Tier 1 (1 SOL → 5 USDC/day) or Tier 2 (10 SOL → 69 USDC/day) and withdraw when your window unlocks.",
  },
  "/admin": { title: "B&W Pay Admin", desc: "", noindex: true },
};

function PageMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const base = META[pathname] || META["/"];
    document.title = base.title;
    const set = (attr, key, val) => {
      let el = document.head.querySelector(`[${attr}="${key}"]`);
      if (val == null) return;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };
    set("name", "description", base.desc);
    set("property", "og:title", base.title);
    set("property", "og:description", base.desc);
    set("name", "robots", base.noindex ? "noindex, nofollow" : "index, follow");
    let link = document.head.querySelector('link[rel="canonical"]');
    if (link) link.setAttribute("href", `https://bnwpay.com${pathname === "/" ? "/" : pathname}`);
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => initLenis(), []);

  return (
    <ErrorBoundary>
      <WalletProvider>
        <BrowserRouter>
          <ScrollManager />
          <PageMeta />
          <div className="grain" aria-hidden="true" />
          <Navbar />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/presale" element={<Presale />} />
            <Route path="/airdrop" element={<AirdropPortal />} />
            <Route path="/mining" element={<Mining />} />
            <Route path="/pay/:id" element={<PayPage />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="*" element={<Landing />} />
          </Routes>
          <Footer />
          <Toaster theme="dark" position="bottom-right" />
        </BrowserRouter>
      </WalletProvider>
    </ErrorBoundary>
  );
}

export default App;
